import React, { useEffect, useState } from 'react';
import { CopyButton } from './CopyButton';
import { SendIcon, EditIcon } from 'lucide-react';
export function PromptResult({
  generatedPrompt,
  formData,
  sendToGrok
}) {
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [customizedPrompt, setCustomizedPrompt] = useState('');
  // Update customized prompt when generated prompt changes
  useEffect(() => {
    setCustomizedPrompt(generatedPrompt);
  }, [generatedPrompt]);
  // Calculate completion percentage for the progress bar
  const calculateCompletion = () => {
    const requiredFields = ['promptType', 'gradeLevel', 'subject', 'topic', 'aiRole'];
    const optionalFields = ['learningObjectives', 'additionalContext', 'timeConstraint', 'outputFormat'];
    // Count filled required fields
    const filledRequiredFields = requiredFields.filter(field => {
      if (field === 'subject' && formData.subject === 'custom') {
        return formData.customSubject && formData.customSubject.trim() !== '';
      }
      return formData[field] && formData[field].trim() !== '';
    }).length;
    // Count filled optional fields
    const filledOptionalFields = optionalFields.filter(field => formData[field] && formData[field].trim() !== '').length;
    // Calculate weighted percentage (required fields count more)
    const requiredWeight = 0.7;
    const optionalWeight = 0.3;
    const requiredPercentage = filledRequiredFields / requiredFields.length * requiredWeight * 100;
    const optionalPercentage = filledOptionalFields / optionalFields.length * optionalWeight * 100;
    return Math.round(requiredPercentage + optionalPercentage);
  };
  const completion = calculateCompletion();
  const handleSendToGrok = promptToSend => {
    if (promptToSend) {
      sendToGrok(promptToSend);
    }
  };
  const toggleCustomize = () => {
    setIsCustomizing(!isCustomizing);
  };
  return <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md border-t-4 border-blue-600 md:sticky md:top-6 lg:top-8" data-id="element-264">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-3" data-id="element-265">
        <h2 className="text-xl font-semibold text-blue-800" data-id="element-266">
          Your Generated Prompt
        </h2>
        <div className="flex flex-wrap gap-2" data-id="element-267">
          <CopyButton textToCopy={isCustomizing ? customizedPrompt : generatedPrompt} data-id="element-268" />
          <button onClick={toggleCustomize} disabled={!generatedPrompt} className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${generatedPrompt ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-269">
            <EditIcon size={16} data-id="element-270" />
            <span data-id="element-271">Customise</span>
          </button>
          <button onClick={() => handleSendToGrok(isCustomizing ? customizedPrompt : generatedPrompt)} disabled={!generatedPrompt} className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${generatedPrompt ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-272">
            <SendIcon size={16} data-id="element-273" />
            <span data-id="element-274">Send to Grok</span>
          </button>
        </div>
      </div>
      <div className="mb-4" data-id="element-275">
        <div className="flex justify-between text-sm mb-1" data-id="element-276">
          <span className="text-gray-600" data-id="element-277">Prompt completion</span>
          <span className="text-blue-600 font-medium" data-id="element-278">{completion}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-279">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 h-2.5 rounded-full" style={{
          width: `${completion}%`
        }} data-id="element-280"></div>
        </div>
        <p className="text-xs text-gray-500 mt-1" data-id="element-281">
          Fill in more fields to enhance your prompt
        </p>
      </div>
      <div className="border border-gray-200 rounded-md p-3 sm:p-4 bg-gray-50 h-[300px] sm:h-[400px] md:max-h-[calc(100vh-320px)] lg:max-h-[calc(100vh-280px)] overflow-auto mb-4" data-id="element-282">
        {generatedPrompt ? <div className="whitespace-pre-line" data-id="element-283">
            <p className="text-gray-800 text-sm sm:text-base" data-id="element-284">
              {generatedPrompt}
            </p>
          </div> : <div className="h-full flex items-center justify-center text-gray-400 italic text-sm sm:text-base" data-id="element-285">
            Your prompt will appear here as you fill out the form
          </div>}
      </div>
      {isCustomizing && generatedPrompt && <div className="mb-4" data-id="element-286">
          <label className="block text-gray-700 font-medium mb-2 text-sm sm:text-base" data-id="element-287">
            Customise Your Prompt
          </label>
          <textarea value={customizedPrompt} onChange={e => setCustomizedPrompt(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 h-36 sm:h-48 focus:outline-none focus:ring-2 focus:ring-indigo-500 mb-3 text-sm sm:text-base" data-id="element-288" />
          <div className="flex justify-end" data-id="element-289">
            <button onClick={() => handleSendToGrok(customizedPrompt)} className="flex items-center gap-1 px-3 py-2 sm:px-4 sm:py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-md text-sm sm:text-base" data-id="element-290">
              <SendIcon size={16} data-id="element-291" />
              <span data-id="element-292">Send Customised Prompt to Grok</span>
            </button>
          </div>
        </div>}
      <div className="bg-blue-50 border border-blue-100 rounded-md p-3 sm:p-4" data-id="element-293">
        <h3 className="text-sm font-medium text-blue-800 mb-2" data-id="element-294">
          How to use this prompt:
        </h3>
        <ol className="text-xs sm:text-sm text-blue-700 list-decimal list-inside space-y-1" data-id="element-295">
          <li data-id="element-296">Complete the form fields to refine your prompt</li>
          <li data-id="element-297">Click "Customise" to make manual edits if needed</li>
          <li data-id="element-298">Copy the prompt using the copy button</li>
          <li data-id="element-299">Send directly to Grok or paste into your preferred AI tool</li>
          <li data-id="element-300">Review the AI's response and adapt for your classroom</li>
        </ol>
      </div>
    </div>;
}